<template>
  <div class="customer-form-page">
    <section class="page-header fade-in">
      <div>
        <h1 class="page-title">Nouveau client</h1>
        <p class="page-subtitle">
          Renseignez les informations du client pour l'ajouter à votre base.
        </p>
      </div>
    </section>

    <section class="fade-in">
      <form class="" @submit.prevent="handleSubmit">
        <div class="">
          <div class="form-row">
            <div class="form-group">
              <label>Nom complet </label>
              <input
                v-model.trim="form.fullName"
                @input="handleFullNameInput"
                type="text"
                class="form-control"
                :class="{ 'has-error': errors.fullName }"
                placeholder="Ex: Jean Dupont"
              />
              <p v-if="errors.fullName" class="error-text">
                {{ errors.fullName }}
              </p>
            </div>

            <div class="form-group">
              <label>Téléphone *</label>
              <input
                v-model.trim="form.phone"
                type="tel"
                class="form-control"
                :class="{ 'has-error': errors.phone }"
                placeholder="Ex: +212 6 00 00 00 00"
              />
              <p v-if="errors.phone" class="error-text">{{ errors.phone }}</p>
            </div>

            <div class="form-group">
              <label>Société</label>
              <input
                v-model.trim="form.society"
                type="text"
                class="form-control"
                placeholder="Ex: Peascom Caisse Manager"
              />
            </div>

            <div class="form-group">
              <label>Plafond impayé</label>
              <input
                v-model="form.maxUnpaid"
                type="number"
                min="0"
                step="0.01"
                class="form-control"
                :class="{ 'has-error': errors.maxUnpaid }"
                placeholder="0.00"
              />
              <p v-if="errors.maxUnpaid" class="error-text">
                {{ errors.maxUnpaid }}
              </p>
            </div>
          </div>

          <div class="form-group">
            <label>Adresse</label>
            <textarea
              v-model.trim="form.address"
              rows="3"
              class="form-control"
              placeholder="Adresse complète"
            ></textarea>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Ville</label>
              <input
                v-model.trim="form.city"
                type="text"
                class="form-control"
                placeholder="Ville"
              />
            </div>
          </div>

          <div class="form-group">
            <label>Remarque</label>
            <textarea
              v-model.trim="form.remark"
              rows="2"
              class="form-control"
              placeholder="Notes internes"
            ></textarea>
          </div>
        </div>

        <div class="form-actions">
          <button
            type="button"
            class="btn btn-light"
            @click="goBack"
            :disabled="submitting"
          >
            Annuler
          </button>
          <button type="submit" class="btn btn-primary" :disabled="submitting">
            <i class="bi bi-check-circle"></i>
            <span v-if="!submitting">Enregistrer</span>
            <span v-else>Enregistrement...</span>
          </button>
        </div>
      </form>
    </section>
  </div>
</template>

<script>
export default {
  name: "CustomerCreate",
  data() {
    return {
      form: {
        reference: "",
        firstName: "",
        lastName: "",
        fullName: "",
        phone: "",
        email: "",
        society: "",
        cin: "",
        maxUnpaid: "",
        ice: "",
        if: "",
        address: "",
        city: "",
        zipCode: "",
        remark: "",
        latitude: "",
        longitude: "",
        employee_reference: "",
        seller_reference: "",
        user_reference: "",
      },
      errors: {},
      submitting: false,
      fullNameTouched: false,
    };
  },
  watch: {
    "form.firstName"() {
      this.autoFillFullName();
    },
    "form.lastName"() {
      this.autoFillFullName();
    },
    "form.fullName"(value) {
      if (!value) {
        this.fullNameTouched = false;
        this.$nextTick(() => this.autoFillFullName());
      }
    },
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    handleFullNameInput() {
      const autoValue = this.composeFullName();
      this.fullNameTouched = this.form.fullName.trim() !== autoValue.trim();
    },
    composeFullName() {
      return [this.form.firstName, this.form.lastName]
        .map((part) => (part || "").trim())
        .filter(Boolean)
        .join(" ");
    },
    autoFillFullName() {
      if (this.fullNameTouched) return;
      const combined = this.composeFullName();
      if (this.form.fullName === combined) return;
      this.form.fullName = combined;
    },
    validate() {
      const errors = {};

      if (!this.form.fullName) {
        errors.fullName = "Le nom du client est requis.";
      }

      if (!this.form.phone) {
        errors.phone = "Le numéro de téléphone est requis.";
      }

      if (this.form.email && !/.+@.+\..+/.test(this.form.email)) {
        errors.email = "Adresse email invalide.";
      }

      if (
        this.form.maxUnpaid !== "" &&
        this.form.maxUnpaid !== null &&
        this.form.maxUnpaid !== undefined
      ) {
        const max = Number(this.form.maxUnpaid);
        if (Number.isNaN(max) || max < 0) {
          errors.maxUnpaid = "Le montant doit être un nombre positif.";
        }
      }

      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    buildPayload() {
      const payload = {
        ...this.form,
        maxUnpaid:
          this.form.maxUnpaid === "" ||
          this.form.maxUnpaid === null ||
          this.form.maxUnpaid === undefined
            ? 0
            : Number(this.form.maxUnpaid),
      };

      if (Number.isNaN(payload.maxUnpaid)) {
        payload.maxUnpaid = 0;
      }

      return payload;
    },
    async handleSubmit() {
      if (!this.validate()) return;

      this.submitting = true;
      try {
        const payload = this.buildPayload();
        await this.$store.dispatch("customer/store", payload);
        this.$store.dispatch("notification/store", {
          status: "success",
          message: "Client créé avec succès.",
        });
        this.$router.push({ name: "customers-list" });
      } catch (error) {
        console.error("Error creating customer", error);
        this.$store.dispatch("notification/store", {
          status: "error",
          message: "Impossible de créer le client.",
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.customer-form-page {
  min-height: 100vh;
  background: var(--bg-secondary);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.page-header .btn {
  margin-bottom: 1rem;
}

.page-title {
  font-size: 2.25rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.page-subtitle {
  margin: 0;
  color: var(--text-secondary);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem;
}

.form-card {
  background: white;
  border-radius: var(--radius-lg);
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.section-title {
  margin: 0;
  font-size: 1.25rem;
  color: var(--text-primary);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

label {
  font-weight: 600;
  color: var(--text-secondary);
}

.helper-text {
  margin: -0.35rem 0 0;
  font-size: 0.82rem;
  color: var(--text-muted, rgba(71, 85, 105, 0.85));
}

input,
textarea {
  border-radius: var(--radius-md);
  border: 1px solid var(--border-color);
  padding: 0.75rem 1rem;
  font-size: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

input:focus,
textarea:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.1);
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.form-actions {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
}

.has-error {
  border-color: var(--danger-color);
}

.error-text {
  color: var(--danger-color);
  font-size: 0.875rem;
  margin: 0;
}

@media (max-width: 1024px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .customer-form-page {
    padding: 1rem;
  }

  .form-card {
    padding: 1.25rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 1.75rem;
  }
}
</style>
